import React, { useState, useEffect, useCallback, useContext } from 'react';
import { EventDetailsPage } from './CreateEventDetails';
import { InstrumentsPage } from './CreateEventInstruments';
import { RegistrationQuestionsPage } from './CreateEventRegistrationQuestions';
import { CreateEvent, EventType } from '../types/Event';
import { Instrument } from '../types/Instrument';
import { CustomRegistrationQuestion, EventRegistrationQuestionBase, RegistrationQuestion } from '../types/RegistrationQuestion';
import { getAllInstruments } from '../services/instrumentService';
import { getEventTypes, sendCreateEventToServer } from '../services/eventService';
import { getAllQuestions } from '../services/registrationQuestionService';
import CreateEventSummaryPage from './CreateEventSummary';
import { useToast } from '../context/ToastContext';
import { addSelectedQuestionsToEvent } from '../services/eventSelectedRegistrationQuestionService';
import { addEventInstruments } from '../services/eventInstrumentService';
import { useNavigate } from 'react-router-dom';
import { EventContext } from '../context/EventContext';
import { UserRoleContext } from '../context/UserRoleContext';
import { addCustomRegistrationQuestionsToEvent } from '../services/customRegistrationQuestionService';
import { ROUTES } from '../constants/routes';
import { MEDIA_FILE_IDS } from '../constants/media-files';
import { formatDateForBackend, formatDateForBackendCreate,  } from '../services/formatter.service';

const CreateEventComponent: React.FC = () => {
  const [eventDetails, setEventDetails] = useState<CreateEvent>({} as CreateEvent);
  const [eventType, setEventType] = useState<string>('');
  const [availableEventTypes, setAvailableEventTypes] = useState<EventType[]>([]);
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [registrationQuestions, setRegistrationQuestions] = useState<RegistrationQuestion[]>([]);
  const [customRegistrationQuestions, setCustomRegistrationQuestions] = useState<CustomRegistrationQuestion[]>([]);
  const [questionOptions, setQuestionOptions] = useState<{ [key: string]: 'Required' | 'Optional' | 'Omit' }>({});
  const [currentPage, setCurrentPage] = useState(0);
  const [isFormValid, setIsFormValid] = useState(false);
  const [TandC, setTandC] = useState<boolean>(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { setCurrentEvent } = useContext(EventContext);
  const { userRole } = useContext(UserRoleContext);


  const handleFormValidityChange = (isValid: boolean) => {
    setIsFormValid(isValid);
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // Fetch data on mount
  useEffect(() => {
    const fetchInstruments = async () => {
      const instrumentArr = await getAllInstruments();
      setInstruments(instrumentArr);
    };

    const fetchEventTypes = async () => {
      const eventTypes = await getEventTypes();
      setAvailableEventTypes(eventTypes);
      setEventType(eventTypes[0]?.event_type_id || '');  // Default to first event type
    };

    const fetchRegistrationQuestions = async () => {
      const questions = await getAllQuestions();

      // Ensure option is one of 'Required', 'Optional', or 'Omit'
      const questionsWithDefaults: RegistrationQuestion[] = questions.map(question => ({
        ...question,
        option: 'Omit' // Set the default option
      }));
      // Now we can safely set the state because the type matches
      setRegistrationQuestions(questionsWithDefaults);
    };

    fetchInstruments();
    fetchEventTypes();
    fetchRegistrationQuestions();
  }, []);


  const handleInstrumentChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const instrumentId = e.target.value;
    setEventDetails((prevDetails) => {
      const selectedInstruments = prevDetails.instrument_ids || [];
      if (e.target.checked) {
        selectedInstruments.push(instrumentId);
      } else {
        const index = selectedInstruments.indexOf(instrumentId);
        if (index > -1) selectedInstruments.splice(index, 1);
      }
      return { ...prevDetails, instrument_ids: selectedInstruments };
    });
  }, []);

  // Example of refactored handleQuestionOptionChange using useCallback
  const handleQuestionOptionChange = useCallback((questionId: string, option: 'Required' | 'Optional' | 'Omit') => {
    setRegistrationQuestions((prevQuestions) =>
      prevQuestions.map((question) =>
        question.preset_registration_question_id === questionId
          ? { ...question, option }
          : question
      )
    );
  }, []);

  const createEvent = async () => {
    try {
      if (userRole?.host_id) {
        eventDetails.host_id = userRole?.host_id
      } else {
        toast.addToast("Please confirm you are associated with a Host!", true);
        return;
      }
      eventDetails.event_type_id = eventType;
      toast.addToast("Creating Event, please don't close the tab");
      // console.log(eventDetails.start_date, eventDetails.end_date);
      // console.log(formatDateForBackendCreate(eventDetails.start_date));
      // return;
      const transformedEventDetails = {
        ...eventDetails,
        start_date: formatDateForBackend(eventDetails.start_date),
        end_date: formatDateForBackend(eventDetails.end_date), // UTC ISO string
        // transform other date fields similarly if needed
      };

      const event = await sendCreateEventToServer(transformedEventDetails);
      if (!event?.event_id) {
        toast.addToast("Error creating event!", true);
        return;
      }

      const questions: EventRegistrationQuestionBase[] = registrationQuestions
        .filter((question) => {
          if (question.option === 'Omit') return false;

          const isMedia = question.preset_registration_question_id in MEDIA_FILE_IDS;
          if (isMedia && !eventDetails.has_media) {
            return false;
          }
          return true;
        })
        .map((question) => ({
          preset_registration_question_id: question.preset_registration_question_id,
          is_required: question.option === 'Required',
        }));

      if (questions.length > 0) {
        const success = await addSelectedQuestionsToEvent(event.event_id, questions);
        if (!success) {
          toast.addToast("Error adding questions to event!", true);
          return;
        }
      }

      if (customRegistrationQuestions.length > 0) {
        const customSuccess = await addCustomRegistrationQuestionsToEvent(event.event_id, customRegistrationQuestions);
        if (!customSuccess) {
          toast.addToast("Error adding custom questions to event!", true);
        }
      }
      if (eventDetails.event_type_id === 'MUSIC' && instruments.length > 0) {
        const instrumentIds = [...new Set(eventDetails.instrument_ids)];
        const instrumentSuccess = await addEventInstruments(event.event_id, instrumentIds);
        if (!instrumentSuccess) {
          toast.addToast("Error adding instruments to event!", true);
          return;
        }
      }
      setCurrentEvent(event);
      toast.addToast("Event Created! You will be redirected momentarily");
      navigate(ROUTES.EVENT_OVERVIEW);
    } catch (error) {
      console.error(error);
      toast.addToast("Error creating event!", true);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, type, value } = e.target;

    let newValue: string | number | boolean = value;

    if (type === 'checkbox') {
      newValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'radio') {
      if (value === 'true') newValue = true;
      else if (value === 'false') newValue = false;
    } else if (type === 'number') {
      newValue = value === '' ? '' : Number(value);
    }

    setEventDetails((prevDetails) => ({
      ...prevDetails,
      [name]: newValue,
    }));
  };


  const pages = [
    <EventDetailsPage
      key="event-details"
      eventDetails={eventDetails}
      eventType={eventType}
      handleInputChange={handleInputChange}
      handleEventTypeChange={(e) => setEventType(e.target.value)}
      availableEventTypes={availableEventTypes}
      onValidityChange={handleFormValidityChange}
      TandC={TandC}
      setTandC={setTandC}
      setEventDetails={setEventDetails}
    />,
    <InstrumentsPage
      key="instruments"
      eventDetails={eventDetails}
      eventType={eventType}
      instruments={instruments}
      handleInstrumentChange={handleInstrumentChange}
    />,
    <RegistrationQuestionsPage
      key="registration-questions"
      eventDetails={eventDetails}
      registrationQuestions={registrationQuestions}
      customRegistrationQuestions={customRegistrationQuestions}
      setCustomRegistrationQuestions={setCustomRegistrationQuestions}
      eventType={eventType}
      handleQuestionOptionChange={handleQuestionOptionChange}
    />,
    <CreateEventSummaryPage
      key="summary-page"
      instruments={instruments}
      eventDetails={eventDetails}
      eventType={eventType}
      registrationQuestions={registrationQuestions}
      customRegistrationQuestions={customRegistrationQuestions}
    />
  ];

  return (
    <div style={{ justifyContent: 'center', display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
      {pages[currentPage]}

      <div className='buttonsRow' style={{ maxWidth: '650px', width: '100%' }}>
        <div className='buttonsLeft'>
          {currentPage > 0 && (<button style={{ marginRight: '10px' }} onClick={() => {
            // If on second page and event type is MUSIC, go back to the first page
            if (eventType != 'MUSIC' && currentPage === 2) {
              setCurrentPage(0); // Go directly to the first page (Event Details)
            } else {
              // Otherwise, go back one page
              setCurrentPage((prev) => Math.max(prev - 1, 0));
            }
          }}>
            Previous
          </button>)}
        </div>
        <div className='buttonsRight'>
          {currentPage < 3 && (<button onClick={() => {
            if (isFormValid) {
              if (eventType != 'MUSIC' && currentPage === 0) {
                // If it's a music event and on the first page, skip directly to the Registration Questions page (i.e., page 2)
                setCurrentPage(2);
              } else {
                // Otherwise, go to the next page normally
                setCurrentPage((prev) => Math.min(prev + 1, pages.length - 1));
              }
            }
          }}
            disabled={!isFormValid}>
            Next
          </button>)}

          {currentPage == 3 && (<button onClick={() => {
            if (isFormValid) {
              createEvent();
            }
          }}
            disabled={!isFormValid}>
            Create Event
          </button>)}
        </div>
      </div>
    </div>
  );
};

export default CreateEventComponent;
